import { useId, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { apiRequest, ApiRequestError } from '../lib/api-client';
import { t } from '../lib/i18n';
import { AuthLayout } from './AuthLayout';
import { ArrowIcon, BuildingIcon, CheckIcon, EyeIcon, LockIcon, MailIcon, UserIcon } from './icons';

interface SignupResponse {
  email: string;
  tenant_slug: string;
}

export function SignupPage(): React.JSX.Element {
  const ids = {
    name: useId(),
    company: useId(),
    email: useId(),
    password: useId(),
  };

  const [form, setForm] = useState({ name: '', companyName: '', email: '', password: '' });
  const [revealed, setRevealed] = useState(false);
  const [result, setResult] = useState<SignupResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [reasons, setReasons] = useState<string[]>([]);
  const [submitting, setSubmitting] = useState(false);

  function update(field: keyof typeof form, value: string): void {
    setForm((current) => ({ ...current, [field]: value }));
  }

  async function handleSubmit(event: FormEvent): Promise<void> {
    event.preventDefault();
    setSubmitting(true);
    setError(null);
    setReasons([]);

    try {
      setResult(
        await apiRequest<SignupResponse>('/v1/auth/signup', { method: 'POST', body: form }),
      );
    } catch (caught) {
      if (caught instanceof ApiRequestError && caught.code === 'auth.weak_password') {
        setReasons((caught.payload?.reasons as string[]) ?? []);
        setError(t('signup.weakPassword'));
      } else if (caught instanceof ApiRequestError && caught.code === 'auth.email_taken') {
        setError(t('signup.emailTaken'));
      } else {
        setError(t('error.generic'));
      }
    } finally {
      setSubmitting(false);
    }
  }

  if (result) {
    return (
      <AuthLayout>
        <div className="auth__done">
          <span className="auth__done-mark" aria-hidden="true">
            <CheckIcon />
          </span>

          <h1>{t('signup.checkInbox')}</h1>
          <p>{t('signup.sentTo').replace('{email}', result.email)}</p>
          <p className="auth__fineprint">{t('signup.restrictedMode')}</p>

          <Link className="button button--primary" to="/entrar">
            {t('login.submit')}
          </Link>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout>
      <header className="auth__heading">
        <h1>{t('signup.title')}</h1>
        <p>{t('signup.subtitle')}</p>
      </header>

      <form onSubmit={(event) => void handleSubmit(event)} noValidate>
        <div className="input">
          <label htmlFor={ids.name}>{t('field.name')}</label>
          <div className="input__control">
            <UserIcon />
            <input
              id={ids.name}
              name="name"
              autoComplete="name"
              autoFocus
              required
              value={form.name}
              onChange={(event) => update('name', event.target.value)}
            />
          </div>
        </div>

        <div className="input">
          <label htmlFor={ids.company}>{t('field.company')}</label>
          <div className="input__control">
            <BuildingIcon />
            <input
              id={ids.company}
              name="companyName"
              autoComplete="organization"
              required
              value={form.companyName}
              onChange={(event) => update('companyName', event.target.value)}
            />
          </div>
        </div>

        <div className="input">
          <label htmlFor={ids.email}>{t('field.email')}</label>
          <div className="input__control">
            <MailIcon />
            <input
              id={ids.email}
              type="email"
              name="email"
              autoComplete="email"
              required
              value={form.email}
              onChange={(event) => update('email', event.target.value)}
            />
          </div>
        </div>

        <div className="input">
          <label htmlFor={ids.password}>{t('field.password')}</label>
          <div className="input__control">
            <LockIcon />
            <input
              id={ids.password}
              type={revealed ? 'text' : 'password'}
              name="password"
              autoComplete="new-password"
              required
              value={form.password}
              onChange={(event) => update('password', event.target.value)}
            />
            <button
              type="button"
              className="input__reveal"
              onClick={() => setRevealed((current) => !current)}
              aria-label={revealed ? t('login.hidePassword') : t('login.showPassword')}
              aria-pressed={revealed}
            >
              <EyeIcon crossed={revealed} />
            </button>
          </div>
          <p className="input__hint">{t('signup.passwordHint')}</p>
        </div>

        {error && (
          <div className="alert" role="alert">
            <p>{error}</p>
            {reasons.length > 0 && (
              <ul>
                {reasons.map((reason) => (
                  <li key={reason}>{t(`password.reason.${reason}`)}</li>
                ))}
              </ul>
            )}
          </div>
        )}

        <button type="submit" className="button button--primary" disabled={submitting}>
          {submitting ? t('signup.submitting') : t('signup.submit')}
          {!submitting && <ArrowIcon />}
        </button>
      </form>

      <p className="auth__switch">
        {t('signup.haveAccount')} <Link to="/entrar">{t('login.submit')}</Link>
      </p>
    </AuthLayout>
  );
}
